import { useQuery } from '@tanstack/react-query';
import { buscarMeuTrabalho, type ItemMeuTrabalho } from '../api/meu-trabalho.js';
import { NOME_ATOR_PLACEHOLDER } from '../lib/ator.js';

const LABEL_TIPO: Record<ItemMeuTrabalho['tipo'], string> = {
  atividade: 'Atividade',
  gate: 'Gate',
  demanda: 'Demanda',
  raid: 'RAID',
  beneficio: 'Benefício',
};

function diasParaPrazo(prazo: string | null): number | null {
  if (!prazo) return null;
  return Math.ceil((new Date(prazo).getTime() - Date.now()) / 86_400_000);
}

function saudacao(hora: number): string {
  if (hora < 12) return 'Bom dia';
  if (hora < 18) return 'Boa tarde';
  return 'Boa noite';
}

function dataFormatada(agora: Date): string {
  // "QUA., 15 DE JUL. DE 2026"
  const segmentos = new Intl.DateTimeFormat('pt-BR', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }).formatToParts(agora);
  const parte = (tipo: Intl.DateTimeFormatPartTypes): string => segmentos.find((p) => p.type === tipo)?.value ?? '';
  const diaSemana = parte('weekday').replace('.', '').toUpperCase();
  const mes = parte('month').replace('.', '').toUpperCase();
  return `${diaSemana}., ${parte('day')} DE ${mes}. DE ${parte('year')}`;
}

function IconeFila(props: { className?: string }) {
  return (
    <svg {...props} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  );
}

function IconeRaidAberto(props: { className?: string }) {
  return (
    <svg {...props} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

// SPEC-UX §2.2, §2.4 — inbox unificado: agrega tudo que espera ação do
// ator, ordenado por prazo. Cabeçalho de saudação vive aqui porque esta é
// a tela de entrada do sistema — não existe home separada.
export function MeuTrabalho() {
  const { data: itens, isLoading } = useQuery({ queryKey: ['meu-trabalho'], queryFn: buscarMeuTrabalho });
  const agora = new Date();
  const totalItens = itens?.length ?? 0;
  const totalRaid = itens?.filter((i) => i.tipo === 'raid').length ?? 0;

  return (
    <div className="p-8">
      <header className="mb-6 border-b border-borda pb-5">
        <div className="fonte-mono mb-2 text-xs uppercase tracking-wide text-grafite">{dataFormatada(agora)}</div>
        <div className="mb-0.5 text-sm text-grafite">{saudacao(agora.getHours())}</div>
        <h1 className="text-2xl font-bold text-ink">{NOME_ATOR_PLACEHOLDER}</h1>
      </header>

      {isLoading && <div className="text-sm text-grafite">Carregando…</div>}

      {!isLoading && (!itens || itens.length === 0) && (
        <div className="flex items-center gap-3.5 rounded-xl border border-borda bg-superficie p-5 shadow-sm">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-status-verde/10 text-status-verde">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <div>
            <div className="mb-0.5 text-[15px] font-semibold text-ink">Tudo em dia</div>
            <div className="text-sm text-grafite">Nenhum risco crítico, tarefa atrasada ou aprovação pendente no momento.</div>
          </div>
        </div>
      )}

      {!isLoading && itens && itens.length > 0 && (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3.5">
            <div className="flex flex-col gap-2.5 rounded-xl border border-borda bg-superficie p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-[10.5px] font-bold uppercase tracking-wide text-grafite">Minha fila</span>
                <div className="flex h-[30px] w-[30px] items-center justify-center rounded-lg bg-aco/15 text-aco-700">
                  <IconeFila />
                </div>
              </div>
              <div className="fonte-mono text-2xl font-bold text-ink">{totalItens}</div>
              <div className="text-xs text-grafite">itens atribuídos a você</div>
            </div>
            <div className="flex flex-col gap-2.5 rounded-xl border border-borda bg-superficie p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-[10.5px] font-bold uppercase tracking-wide text-grafite">RAID aberto</span>
                <div className="flex h-[30px] w-[30px] items-center justify-center rounded-lg bg-status-ambar/15 text-status-ambar">
                  <IconeRaidAberto />
                </div>
              </div>
              <div className="fonte-mono text-2xl font-bold text-ink">{totalRaid}</div>
              <div className="text-xs text-grafite">itens RAID sob sua responsabilidade</div>
            </div>
          </div>

          <ul className="flex flex-col gap-2">
            {itens.map((item) => {
              const dias = diasParaPrazo(item.prazo);
              const urgente = dias !== null && dias <= 2;
              return (
                <li key={`${item.tipo}_${item.id}`} className="flex items-center justify-between rounded border border-borda bg-superficie px-4 py-3">
                  <div>
                    <span className="fonte-mono mr-2 text-xs text-grafite">{LABEL_TIPO[item.tipo]}</span>
                    <span className="text-sm text-ink">{item.titulo}</span>
                  </div>
                  {dias !== null && (
                    <span className={`fonte-mono text-xs ${urgente ? 'font-medium text-status-vermelho' : 'text-grafite'}`}>
                      {dias >= 0 ? `${dias}d` : `atrasado ${-dias}d`}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}
